/**
 * Touch controls: floating joystick (left zone), camera drag (right zone), context
 * action buttons with icons, labels and cooldown sweeps. Left-handed layout supported.
 */
import { Vector2 } from 'three';
import type { Action, Input } from '@core/input';
import type { ActionLabelKey } from '@core/tree';
import { el, escapeHtml } from '../dom';
import { ICONS } from '../icons';

/** Drag distance (px, before touch scale) for full speed — small so the stick feels immediate. */
const RADIUS = 46;
const DEADZONE = 0.08;

export class TouchControls {
  readonly root = el('div', 'touch');
  private stick = el('div', 'stick idle', '<div class="arrows"><i></i><i></i><i></i><i></i></div><div class="knob"></div>');
  private knob: HTMLElement;
  private buttons = el('div', 'buttons');
  private btnEls = new Map<ActionLabelKey, HTMLElement>();
  private moveId: number | null = null;
  private lookId: number | null = null;
  private origin = new Vector2();
  private lastLook = new Vector2();
  private v = new Vector2();
  private scale = 1;
  onPress?: (a: ActionLabelKey, down: boolean) => void;

  constructor(private readonly input: Input) {
    this.knob = this.stick.querySelector('.knob')!;
    const move = el('div', 'zone-move');
    const look = el('div', 'zone-look');
    this.root.append(look, move, this.stick, this.buttons);
    this.resetStick();

    move.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.moveId !== null) return;
      this.moveId = e.pointerId;
      move.setPointerCapture(e.pointerId);
      this.origin.set(e.clientX, e.clientY);
      this.placeStick(e.clientX, e.clientY);
      this.stick.classList.remove('idle');
      this.input.device = 'touch';
      this.input.setVirtualMove(new Vector2(0, 0));
    });
    move.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.moveId) return;
      const r = RADIUS * this.scale;
      let dx = e.clientX - this.origin.x;
      let dy = e.clientY - this.origin.y;
      const len = Math.hypot(dx, dy);
      // Drag the base along when the finger leaves the ring (floating stick).
      if (len > r * 1.4) {
        const k = (len - r * 1.4) / len;
        this.origin.x += dx * k;
        this.origin.y += dy * k;
        this.placeStick(this.origin.x, this.origin.y);
        dx = e.clientX - this.origin.x;
        dy = e.clientY - this.origin.y;
      }
      this.v.set(dx, -dy).divideScalar(r);
      // Dead zone rescaled so output ramps from 0 right at its edge (no dead band, no jump).
      const mag = Math.min(1, this.v.length());
      if (mag < DEADZONE) this.v.set(0, 0);
      else this.v.setLength((mag - DEADZONE) / (1 - DEADZONE));
      this.knob.style.transform = `translate(${this.v.x * r * 0.9}px, ${-this.v.y * r * 0.9}px)`;
      this.input.setVirtualMove(this.v.clone());
    });
    const endMove = (e: PointerEvent) => {
      if (e.pointerId !== this.moveId) return;
      this.moveId = null;
      this.input.setVirtualMove(null);
      this.resetStick();
    };
    move.addEventListener('pointerup', endMove);
    move.addEventListener('pointercancel', endMove);

    look.addEventListener('pointerdown', (e) => {
      if (this.lookId !== null) return;
      this.lookId = e.pointerId;
      look.setPointerCapture(e.pointerId);
      this.lastLook.set(e.clientX, e.clientY);
      this.input.device = 'touch';
    });
    look.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.lookId) return;
      const w = Math.max(1, window.innerWidth);
      this.input.addLook(((e.clientX - this.lastLook.x) / w) * 1.6, ((e.clientY - this.lastLook.y) / w) * 1.6);
      this.lastLook.set(e.clientX, e.clientY);
    });
    const endLook = (e: PointerEvent) => {
      if (e.pointerId === this.lookId) this.lookId = null;
    };
    look.addEventListener('pointerup', endLook);
    look.addEventListener('pointercancel', endLook);
  }

  private placeStick(x: number, y: number): void {
    this.stick.style.left = `${x}px`;
    this.stick.style.top = `${y}px`;
  }

  /** Park a faint stick at its resting spot so players know where to touch. */
  private resetStick(): void {
    this.knob.style.transform = '';
    this.stick.classList.add('idle');
    const left = this.root.classList.contains('left-handed');
    const x = left ? window.innerWidth - 110 * this.scale : 110 * this.scale;
    this.placeStick(x, window.innerHeight - 120 * this.scale);
  }

  setLayout(opts: { scale: number; leftHanded: boolean }): void {
    this.scale = opts.scale;
    this.root.style.setProperty('--touch-scale', String(opts.scale));
    this.root.classList.toggle('left-handed', opts.leftHanded);
    if (this.moveId === null) this.resetStick();
  }

  releaseAll(): void {
    this.moveId = null;
    this.lookId = null;
    this.input.setVirtualMove(null);
    for (const [k, b] of this.btnEls) {
      b.classList.remove('down');
      this.input.setAction(k as Action, false, `touch:${k}`);
    }
    this.resetStick();
  }

  setActions(labels: Partial<Record<ActionLabelKey, string>>): void {
    this.buttons.innerHTML = '';
    this.btnEls.clear();
    const order: ActionLabelKey[] = ['interact', 'ability', 'jump', 'dash', 'attack'];
    for (const k of order) {
      const label = labels[k];
      // 'Evolve' is driven by the evolve prompt; any other interact label gets a Use button (#31).
      if (!label || (k === 'interact' && /^evolve$/i.test(label))) continue;
      const b = el('div', `tbtn ready pos-${k}${k === 'attack' ? ' primary' : ''}`, `<div class="cd"></div>${ICONS[k]}<span>${escapeHtml(label)}</span>`);
      // If there's no attack button, promote the first available action to the big slot.
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        b.setPointerCapture(e.pointerId);
        b.classList.add('down');
        this.input.device = 'touch';
        this.input.setAction(k as Action, true, `touch:${k}`);
        this.onPress?.(k, true);
        navigator.vibrate?.(8);
      });
      const up = () => {
        b.classList.remove('down');
        this.input.setAction(k as Action, false, `touch:${k}`);
        this.onPress?.(k, false);
      };
      b.addEventListener('pointerup', up);
      b.addEventListener('pointercancel', up);
      this.buttons.append(b);
      this.btnEls.set(k, b);
    }
    if (!labels.attack && this.buttons.firstChild) {
      const first = [...this.btnEls.values()].pop()!;
      first.className = first.className.replace(/pos-\w+/, '') + ' primary';
    }
  }

  /** Hide the contextual Use button while the evolve prompt owns 'interact'. */
  setEvolveVisible(v: boolean): void {
    this.btnEls.get('interact')?.classList.toggle('hidden', v);
  }

  setCooldown(a: ActionLabelKey, frac: number): void {
    const b = this.btnEls.get(a);
    if (!b) return;
    b.style.setProperty('--cd', String(frac));
    b.classList.toggle('ready', frac <= 0);
  }
}
